import { AlignmentType, Document, Packer, Paragraph, TextRun } from 'docx'
import { fmtThaiDateRange, formatYmd } from '.'

type Align = (typeof AlignmentType)[keyof typeof AlignmentType]

export type ExportAgenda = {
    agendaId: number
    agendaNo: string
    agendaName?: string
    meetingDate: string
    meetingStartTime?: string
    meetingEndTime?: string
    meetingLocation?: string
    topics?: TopicNode[]
}

export type TopicNode = {
    topicId?: number | string
    topicName?: string
    topicDescription?: string | null
    children?: TopicNode[]
    projects?: Array<{
        projectId: number | string
        projectNumber?: string | null
        projectDescription: string
    }>
}

const FONT_FAMILY = 'TH SarabunPSK'
const INDENT_UNIT = 720

const stripOutlinePrefix = (line: string) => line.replace(/^\d+(?:\.\d+)+\s+/, '').trim()

const P = (text: string, align?: Align, indentLeftTwip = 0, size = 28) =>
    new Paragraph({
        alignment: align,
        indent: indentLeftTwip ? { left: indentLeftTwip } : undefined,
        spacing: { after: 120 },
        children: [new TextRun({ text, font: FONT_FAMILY, size })],
    })

const PBold = (text: string, align?: Align, size = 32) =>
    new Paragraph({
        alignment: align,
        spacing: { after: 120 },
        children: [new TextRun({ text, bold: true, font: FONT_FAMILY, size })],
    })

const PFirstLine = (text: string, align?: Align, firstLineTwip = 720, size = 28) =>
    new Paragraph({
        alignment: align,
        indent: { firstLine: firstLineTwip },
        spacing: { after: 120 },
        children: [new TextRun({ text, font: FONT_FAMILY, size })],
    })

const depthFromNumbering = (numPath: string) => Math.max(0, (numPath?.split('.').length ?? 1) - 1)

const renderProjects = (projects: TopicNode['projects'], numberingPath: string, acc: Paragraph[]) => {
    if (!projects || projects.length === 0) return
    const depth = depthFromNumbering(numberingPath)
    const left = INDENT_UNIT * (1 + depth)
    projects.forEach((proj) => {
        const no = proj.projectNumber || proj.projectId
        if (no != null && String(no).trim() !== '') {
            acc.push(P(`โครงการวิจัยเลขที่ ${no}`, undefined, left))
        }
        const rawDesc = proj.projectDescription ?? ''
        rawDesc
            .split(/\r?\n/)
            .map((line) => stripOutlinePrefix(line))
            .filter((line) => line !== '')
            .forEach((line) => {
                acc.push(P(line, undefined, left))
            })
        acc.push(P('', undefined, left))
    })
}

const renderTopics = (topics: TopicNode[] | undefined, acc: Paragraph[], prefix: string[] = []) => {
    if (!topics || topics.length === 0) return
    topics.forEach((topic, i) => {
        const idx = (i + 1).toString()
        const nextPrefix = [...prefix, idx]
        const isTop = prefix.length === 0
        const numbering = nextPrefix.join('.')
        if (isTop) {
            acc.push(P(`วาระที่ ${idx} ${topic.topicName ?? ''}`))
            if (topic.topicDescription) acc.push(PFirstLine(topic.topicDescription, 'both', 720))
            renderProjects(topic.projects, idx, acc)
        } else {
            acc.push(P(`วาระย่อยที่ ${numbering} ${topic.topicName ?? ''}`, undefined, 720))
            if (topic.topicDescription) acc.push(PFirstLine(topic.topicDescription, 'both', 1440))
            renderProjects(topic.projects, numbering, acc)
        }
        if (topic.children?.length) renderTopics(topic.children, acc, nextPrefix)
    })
}

export const buildAgendaDocxBlob = async (agenda: ExportAgenda): Promise<Blob> => {
    const beYear = new Date(agenda.meetingDate).getFullYear() + 543
    const when = fmtThaiDateRange(agenda.meetingDate, agenda.meetingStartTime, agenda.meetingEndTime)
    const children: Paragraph[] = [
        PBold('ระเบียบวาระการประชุม', AlignmentType.CENTER, 36),
        P(agenda.agendaName || 'คณะกรรมการพิจารณาจริยธรรมการวิจัยในคน กลุ่มสหสถาบันชุดที่ 1 จุฬาลงกรณ์มหาวิทยาลัย', AlignmentType.CENTER),
        P(`ครั้งที่ ${agenda.agendaNo} ${when}`, AlignmentType.CENTER),
        P(agenda.meetingLocation ?? '', AlignmentType.CENTER),
        P('—'.repeat(25), AlignmentType.CENTER, 0, 20),
    ]
    renderTopics(agenda.topics, children)
    const doc = new Document({
        sections: [
            {
                properties: { page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } } },
                children,
            },
        ],
    })
    return Packer.toBlob(doc)
}

export const downloadAgendaDocx = async (agenda: ExportAgenda) => {
    const blob = await buildAgendaDocxBlob(agenda)
    const dateStr = formatYmd(agenda.meetingDate || new Date())
    const fileName = `ระเบียบวาระการประชุม_${dateStr}.docx`
    const a = document.createElement('a')
    a.href = URL.createObjectURL(blob)
    a.download = fileName
    a.click()
    URL.revokeObjectURL(a.href)
}
